// CZ: MTPL insurance limits (in CZK) of an insurer, maintained by the broker
// as the fleet vehicle default setting "mtplInsuranceLimits":
//   attributeValue = '[{"personalInjury":35000000,"propertyDamage":35000000},...]'
//   settingCriteria = {} (the limits apply to all vehicles of the insurer)
// The client picks one of them for the requested MTPL; the coverage request
// (clientCoveragesInfo) stores it as "{personal injury}/{property damage}".

import { FleetDefaultSetting } from "types/types";
import {
  findApplicableClientOptionSetting,
  parseClientOptionList,
} from "./clientOptionSettings";

export const MTPL_INSURANCE_LIMITS_ATTRIBUTE_NAME = "mtplInsuranceLimits";

export interface MtplInsuranceLimit {
  personalInjury: number;
  propertyDamage: number;
}

const isPositiveInteger = (v: any) => Number.isInteger(v) && v > 0;

/** The limits of a setting's attributeValue; [] if it is not such a JSON list. */
export const parseMtplInsuranceLimits = (
  attributeValue: string | number | null | undefined,
): MtplInsuranceLimit[] =>
  parseClientOptionList(attributeValue).filter(
    (l) =>
      isPositiveInteger(l?.personalInjury) &&
      isPositiveInteger(l?.propertyDamage),
  );

/** "{personal injury}/{property damage}" - the display and the stored format. */
export const formatMtplInsuranceLimit = (limit: MtplInsuranceLimit): string =>
  `${limit.personalInjury}/${limit.propertyDamage}`;

/**
 * The limits ("{personal injury}/{property damage}") the client can pick for
 * an MTPL requested from `coverageFrom` ("YYYY-MM-DD"): those of the insurer's
 * "mtplInsuranceLimits" setting with the latest valid from date that is not
 * after the coverage start. [] when there is no insurer, no coverage start or
 * no such setting.
 */
export const getMtplInsuranceLimitOptions = (
  fleetDefaultSettings: FleetDefaultSetting[] | null | undefined,
  insurerPartnerNumber: number | string | null | undefined,
  coverageFrom: string | null | undefined,
): string[] => {
  const applicable = findApplicableClientOptionSetting(
    fleetDefaultSettings,
    MTPL_INSURANCE_LIMITS_ATTRIBUTE_NAME,
    insurerPartnerNumber,
    coverageFrom,
  );
  if (!applicable) return [];
  return Array.from(
    new Set(
      parseMtplInsuranceLimits(applicable.attributeValue).map(
        formatMtplInsuranceLimit,
      ),
    ),
  );
};
